import { Alert, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useAuthContext } from '@/components/Auth/AuthContext';
import { Border, Color, FontFamily, FontSize, Padding } from '@/GlobalStyles';
import { Text } from 'react-native-paper';
import TextInput from '@/components/Form/TextInput';
import { AvailableValidationRules } from '@/components/Form/validation';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useForm } from 'react-hook-form';
import Form from '@/components/Form/Form';
import * as Auth from 'aws-amplify/auth';
import ErrorMap from '@/components/Auth/ErrorMap';
import SimpleLink from '@/components/SimpleLink';
import Button from '@/components/Button';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type SignupForm = {
  email: string;
  nom: string;
  prenom: string;
  password: string;
};

type ConfirmationForm = {
  email?: string;
  code: string;
};

const SignupPage = () => {
  const { step } = useLocalSearchParams();
  const authInfos = useAuthContext();
  const { height, width } = useWindowDimensions();

  const router = useRouter();
  const { t } = useTranslation();

  const [user, setUser] = useState<any>();
  const [isSigningUp, setIsSigningUp] = useState(false);

  const signUpForm = useForm<SignupForm>();
  const confirmationForm = useForm<ConfirmationForm>();

  if (authInfos.jwt) {
    return <Redirect href="/patients" />;
  }

  return (
    <View style={[styles.screen, { height }]}>
      <ScrollView
        contentContainerStyle={[styles.loginInner, { paddingHorizontal: 40 }]}
        style={{ flex: 1 }}
      >
        <SafeAreaView style={styles.logoParent}>
          {user || step == '2' ? (
            <Form {...confirmationForm}>
              <Text style={styles.title}>{t('Finalisation de votre compte')}</Text>
              <View style={styles.frameParent}>
                {step == '2' ? (
                  <TextInput
                    name="email"
                    label={'E-mail'}
                    placeholder={'Votre adresse e-mail'}
                    validators={[AvailableValidationRules.email, AvailableValidationRules.required]}
                  />
                ) : null}
                <TextInput
                  name="code"
                  label={'Code de confirmation'}
                  placeholder={'Votre code de confirmation reçu par e-mail'}
                  validators={[AvailableValidationRules.required]}
                />
                <Button
                  loading={isSigningUp}
                  disabled={isSigningUp}
                  withShadow={false}
                  style={styles.btnForm}
                  onPress={confirmationForm.handleSubmit(async (data) => {
                    setIsSigningUp(true);
                    try {
                      await Auth.confirmSignUp({
                        username: data.email || user,
                        confirmationCode: data.code,
                      });
                      await Auth.autoSignIn();
                      setIsSigningUp(false);
                    } catch (error: any) {
                      setIsSigningUp(false);
                      Alert.alert(t('Une erreur est survenue'), ErrorMap(error));
                    }
                  })}
                >
                  {'Confirmer'}
                </Button>
                <View style={[styles.infosRapides, styles.marginInfosRapidesTop]}>
                  <View style={[styles.texte, styles.texteFlexBox]}>
                    <Text style={styles.vousSouhaitezRejoindre}>
                      {t('Vous avez déjà un compte ?')}
                    </Text>
                    <SimpleLink href={'login'}>{'Connectez-vous'}</SimpleLink>
                  </View>
                </View>
              </View>
            </Form>
          ) : (
            <Form {...signUpForm}>
              <Text style={styles.title}>{t('Création de votre compte')}</Text>
              <View style={styles.frameParent}>
                <TextInput
                  name="email"
                  label={'E-mail'}
                  placeholder={'Votre adresse e-mail'}
                  validators={[AvailableValidationRules.email, AvailableValidationRules.required]}
                />
                <TextInput
                  name="password"
                  label={'Mot de passe'}
                  placeholder={'Votre mot de passe'}
                  secureTextEntry
                  validators={[
                    AvailableValidationRules.required,
                    {
                      rule: AvailableValidationRules.password,
                      errorMessage:
                        t(
                          'Votre mot de passe doit contenir au minimum 8 charactères dont un chiffre, une minuscule, une majuscule et un caractère special de la liste suivante :'
                        ) + ' ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < \' : ; | _ ~ ` + =',
                    },
                  ]}
                />
                <TextInput
                  name="prenom"
                  label={'Prénom'}
                  placeholder={'Votre prénom'}
                  validators={[AvailableValidationRules.required]}
                />
                <TextInput
                  name="nom"
                  label={'Nom'}
                  placeholder={'Votre nom'}
                  validators={[AvailableValidationRules.required]}
                />
                <Button
                  loading={isSigningUp}
                  disabled={isSigningUp}
                  withShadow={false}
                  style={styles.btnForm}
                  onPress={signUpForm.handleSubmit(async (data) => {
                    setIsSigningUp(true);
                    try {
                      const user = await Auth.signUp({
                        password: data.password,
                        username: data.email,
                        options: {
                          userAttributes: {
                            email: data.email,
                            given_name: data.prenom,
                            family_name: data.nom,
                          },
                          autoSignIn: {
                            enabled: true,
                          },
                        },
                      });
                      setUser(data.email);
                      setIsSigningUp(false);
                    } catch (error: any) {
                      setIsSigningUp(false);
                      console.log(error);
                      Alert.alert(t('Une erreur est survenue'), ErrorMap(error));
                    }
                  })}
                >
                  {'Créer votre compte'}
                </Button>
                <View style={[styles.infosRapides, styles.marginInfosRapidesTop]}>
                  <View style={[styles.texte, styles.texteFlexBox]}>
                    <Text style={styles.vousSouhaitezRejoindre}>
                      {t('Vous avez déjà un compte ?')}
                    </Text>
                    <SimpleLink href={'login'}>{'Connectez-vous'}</SimpleLink>
                  </View>
                </View>
              </View>
            </Form>
          )}
        </SafeAreaView>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  texteFlexBox: {
    marginTop: 20,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  loginImage: {
    alignSelf: 'stretch',
    overflow: 'hidden',
  },
  logoIcon: {
    width: 150,
    height: 80,
    alignSelf: 'center',
  },
  motDePasseContainer: {
    justifyContent: 'flex-end',
    alignSelf: 'flex-end',
  },
  motDePasse: {
    fontSize: FontSize.p_size,
    lineHeight: 24,
    color: Color.black,
    textAlign: 'right',
    opacity: 0.3,
    marginBottom: 14,
    fontFamily: FontFamily.labelMedium,
    alignSelf: 'stretch',
  },
  iconsmall: {
    width: 22,
    height: 22,
  },
  vousSouhaitezRejoindre: {
    fontSize: FontSize.labelMedium_size,
    lineHeight: 18,
    fontWeight: '500',
    color: Color.MainNoir,
    textAlign: 'center',
    display: 'flex',
    width: 162,
    fontFamily: FontFamily.labelMedium,
    justifyContent: 'center',
    alignItems: 'center',
  },
  texte: {
    paddingHorizontal: 0,
    paddingVertical: Padding.p_11xs,
  },
  marginInfosRapidesTop: {
    marginTop: 35,
  },
  marginInfosRapidesBottom: {
    marginBottom: 35,
  },
  infosRapides: {
    padding: Padding.p_11xl,
    borderWidth: 1,
    borderColor: Color.colorGray_100,
    borderRadius: Border.br_3xs,
    borderStyle: 'solid',
    alignItems: 'center',
    justifyContent: 'center',
  },
  frameParent: {
    marginTop: 50,
  },
  logoParent: {
    maxWidth: 500,
    width: '100%',
  },
  loginInner: {
    paddingVertical: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  screen: {
    flexDirection: 'row',
    flex: 1,
    overflow: 'hidden',
  },
  title: {
    marginTop: 20,
    fontFamily: FontFamily.labelBold,
    fontSize: FontSize.h3_size,
    color: Color.MainNoir,
    textAlign: 'center',
  },
  btnForm: {
    marginTop: 20,
  },
});

export default SignupPage;
